import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Presentation,
  CalendarHeart,
  Users,
  Coffee,
  Wifi,
  CarFront,
  Sparkles,
  ArrowUpRight,
  Phone,
  Mail,
} from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { FaqAccordion, buildFaqLd } from "../components/FaqAccordion";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { Picture, findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";
import { PHONE_TEL, PHONE_DISPLAY } from "../components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "Centerville@westbridgehotels.com";

const BUSINESS_FEATURES = [
  { icon: Presentation, label: "Projector-equipped meeting room", body: "Bring a laptop and an HDMI cable — the projector and screen are set. Wi-Fi is free." },
  { icon: Wifi, label: "Free high-speed Wi-Fi", body: "Reliable enough for a full day of video calls. If speeds drop, the front desk troubleshoots in person." },
  { icon: CarFront, label: "Free parking for the whole team", body: "Large, well-lit lot — no valet, no daily rate, no drama. Fine for a fleet vehicle or a rented van." },
  { icon: Coffee, label: "Free continental breakfast", body: "Belgian waffles, fresh fruit, bagels, and hot coffee for anyone staying overnight — a warm way to start a training day." },
];

const CELEBRATION_FEATURES = [
  { icon: CalendarHeart, label: "A quiet meeting room for a gathering", body: "Round or rectangular table setups. Chair covers with color sashes on request. Not a ballroom — an honest, warm room for a real celebration." },
  { icon: Users, label: "Group room blocks next door", body: "For out-of-town family. Direct block booking through the front desk — no third-party group tools between you and your rate." },
  { icon: Sparkles, label: "King Jacuzzi Suite for the guests of honor", body: "The anniversary couple. The birthday guest. The parents of the shower honoree. Our largest, quietest room with an in-room jacuzzi tub." },
  { icon: Coffee, label: "Breakfast the morning after", body: "Free continental breakfast in the lobby for every room in the block. A warm, easy morning-after with the family that stayed the night." },
];

const FAQS = [
  { q: "How big is the meeting room?", a: "It's a small-to-mid-sized meeting room suitable for a small business meeting, training day, shower, or celebration. Exact fit depends on your setup (theater rows vs. classroom vs. banquet rounds) — call the front desk and describe your event, and we'll walk you through what actually fits." },
  { q: "What comes with the room?", a: "A projector and screen, tables and chairs (chair covers with color sashes available on request), free Wi-Fi, and access to lobby coffee during breakfast hours. Bring your own laptop / HDMI cable. We don't provide catering or bar service — the historic square is two blocks away for lunch runs." },
  { q: "Do you have catering or a bar?", a: "We don't run in-house catering or bar service. Many of our guests bring in a caterer from Centerville or Ottumwa, or run a light continental setup themselves. The front desk can point you to local restaurants and bakeries that handle small-event platters — the historic square is close." },
  { q: "Can we get group rates on rooms for out-of-town guests?", a: "Yes. Direct block booking with the front desk is simpler and better priced than a third-party group tool. Call (641) 437-7272 with your dates and estimated room count — we'll set up a block your guests can reference by name at booking." },
  { q: "What's the cost of the meeting room?", a: "Rates depend on your setup, timing, and whether you're blocking guest rooms alongside. Call the front desk at (641) 437-7272 — we quote on a per-event basis rather than a one-size-fits-all published rate." },
  { q: "Can we host a wedding shower, baby shower, or birthday party?", a: "Yes — small showers, birthdays, retirement gatherings, and family celebrations are among the most common uses of the room. Confirm dates with the front desk and let us know how many people you're expecting." },
  { q: "Can we host a small wedding reception?", a: "We can host a small reception — but we're not a ballroom, and we don't do full-service weddings. If you're expecting a smaller group and a simple setup, call to talk through your plan with the front desk." },
  { q: "Is the meeting room ADA-accessible?", a: "Yes. The property is in substantial compliance with the 2010 ADA Standards. The meeting room is on an accessible route from marked accessible parking through the public entrance." },
];

export default function MeetingsEvents() {
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/photos`).then((r) => { if (alive) setPhotos(r.data.photos || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const heroPhoto = useMemo(() => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-conference-room-wide") || findPhoto(photos, "westbridge-inn-suites-centerville-iowa-conference-room"), [photos]);
  const meetingPhoto = useMemo(() => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-conference-room"), [photos]);
  const suitePhoto = useMemo(() => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-king-suite-spa-bath") || findPhoto(photos, "westbridge-inn-suites-centerville-iowa-jacuzzi-suite"), [photos]);
  const canonical = "https://westbridgehotels.com/meetings-events";

  useSeo({
    title: "Meetings & Events in Centerville, Iowa — Westbridge Inn & Suites",
    description: "A projector-equipped meeting room for small business meetings, showers, birthdays, and family celebrations in Centerville, Iowa. Call the front desk to plan.",
    canonical,
    image: heroPhoto ? `${window.location.origin}${heroPhoto.jpg}` : undefined,
    jsonLd: [
      buildBreadcrumbLd([{ label: "Meetings & Events", to: "/meetings-events" }]),
      buildFaqLd(FAQS),
    ],
  });

  return (
    <main id="main" data-testid="meetings-events-page">
      <PageHero
        eyebrow="Meetings & Events"
        title="A meeting room for the team. A quiet room for a celebration."
        intro="A projector-equipped room in the largest and newest lodging in southern Iowa — for small business meetings, showers, birthdays, anniversaries, and family gatherings. Not a convention center. An honest room, in a warm hotel, two blocks off the historic square."
        photo={heroPhoto}
        breadcrumbs={[{ label: "Meetings & Events", to: "/meetings-events" }]}
        testId="meetings-hero"
      />

      {/* Quick CTA pair */}
      <section className="bg-cream py-10 md:py-12 border-b border-tan" data-testid="meetings-quick-cta">
        <div className="container-app grid md:grid-cols-2 gap-6 md:gap-10 items-center">
          <div>
            <p className="eyebrow text-brass mb-2">One phone call</p>
            <h2 className="font-display text-2xl md:text-3xl text-forest-900 leading-tight">
              We plan meetings and events person-to-person — not through a form.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <a href={`tel:${PHONE_TEL}`} className="btn-primary" data-testid="meetings-call-cta">
              <Phone size={16} /> Call {PHONE_DISPLAY}
            </a>
            <a href={`mailto:${EMAIL}?subject=Meeting%20or%20event%20inquiry`} className="btn-ghost-dark" data-testid="meetings-email-cta">
              <Mail size={16} /> Email the front desk
            </a>
          </div>
        </div>
      </section>

      {/* ============================ FOR BUSINESS ============================ */}
      <section className="bg-cream py-20 md:py-28" data-testid="for-business-section">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="md:col-span-5 md:sticky md:top-28">
            <p className="eyebrow text-brass mb-4">For business</p>
            <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] text-forest-900 tracking-tight">
              A meeting room, quiet guest rooms, and free parking — no rental theatrics.
            </h2>
            <p className="mt-6 text-ink/75 leading-relaxed">
              Small team offsites. Local business meetings. Regional training days. Corporate half-days for teams driving in from Des Moines, Ottumwa, or Kirksville. Book the meeting room, block a handful of interior-corridor rooms next door, and we'll do the rest quietly. Free continental breakfast the morning of.
            </p>
            <a href="/stay/small-business-meetings" className="mt-8 inline-flex items-center gap-2 brass-underline text-forest-900" data-testid="business-more-link">
              See the small business meetings guide <ArrowUpRight size={14} />
            </a>
          </div>
          <div className="md:col-span-7">
            {meetingPhoto && (
              <div className="aspect-[16/10] overflow-hidden border border-tan mb-8">
                <Picture photo={meetingPhoto} className="block w-full h-full" imgClassName="w-full h-full object-cover" sizes="(min-width: 768px) 55vw, 100vw" />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
              {BUSINESS_FEATURES.map((f) => (
                <div key={f.label} className="flex gap-4" data-testid={`business-feature-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <f.icon size={22} className="text-brass mt-1 flex-none" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-display text-lg text-forest-900 mb-1.5">{f.label}</h3>
                    <p className="text-sm text-ink/75 leading-relaxed">{f.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 bg-cream-100 border border-brass/40 p-6">
              <p className="eyebrow text-brass mb-2">Corporate & repeat local business</p>
              <p className="text-forest-900 leading-relaxed">
                Call for group and corporate rates. We set up direct-billing for established local accounts and repeat regional businesses — one point of contact, one preferred rate. Ask the front desk to walk you through the paperwork.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ FOR CELEBRATIONS ============================ */}
      <section className="bg-forest-900 text-cream py-20 md:py-28" data-testid="for-celebrations-section">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="md:col-span-7 order-2 md:order-1">
            {suitePhoto && (
              <div className="aspect-[16/10] overflow-hidden border border-forest-700 mb-8">
                <Picture photo={suitePhoto} className="block w-full h-full" imgClassName="w-full h-full object-cover" sizes="(min-width: 768px) 55vw, 100vw" />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
              {CELEBRATION_FEATURES.map((f) => (
                <div key={f.label} className="flex gap-4" data-testid={`celebration-feature-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <f.icon size={22} className="text-brass-300 mt-1 flex-none" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-display text-lg text-cream mb-1.5">{f.label}</h3>
                    <p className="text-sm text-cream/75 leading-relaxed">{f.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 bg-forest-800 border border-forest-700 p-6">
              <p className="eyebrow text-brass-300 mb-2">A note on what we don't do</p>
              <p className="text-cream/85 leading-relaxed">
                We don't run in-house catering, a florist, or a bar. We don't promise champagne on ice, chocolate strawberries, or rose petals. What we do: keep the room quiet, keep the rate honest, and help you find the local shops on the square that handle the rest. Call us before you arrive and tell us what you're planning — the front desk will do their best to help.
              </p>
            </div>
          </div>
          <div className="md:col-span-5 order-1 md:order-2 md:sticky md:top-28">
            <p className="eyebrow text-brass-300 mb-4">For celebrations</p>
            <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] tracking-tight">
              Birthdays, showers, anniversaries, and small receptions.
            </h2>
            <p className="mt-6 text-cream/80 leading-relaxed">
              A wedding shower for eighteen. A retirement gathering for thirty. A birthday brunch for the whole family. A small reception the night before a courthouse wedding. The room and the rate flex to fit; the meeting-room-meets-family-room feel doesn't.
            </p>
            <div className="mt-8 space-y-3">
              <Link to="/stay/couples-special-occasions" className="inline-flex items-center gap-2 text-brass-300 brass-underline" data-testid="celebrations-couples-link">
                For couples &amp; special occasions <ArrowUpRight size={14} />
              </Link>
              <br />
              <Link to="/rooms/king-jacuzzi-suite" className="inline-flex items-center gap-2 text-brass-300 brass-underline" data-testid="celebrations-jacuzzi-link">
                See the King Jacuzzi Suite <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ FAQ ============================ */}
      <section className="bg-cream-100 py-20 md:py-28 paper-grain" data-testid="meetings-faq-section">
        <div className="container-app relative grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-4">
            <p className="eyebrow mb-3">Common questions</p>
            <h2 className="font-display font-light text-2xl md:text-4xl text-forest-900 tracking-tight leading-[1.1]">
              Real answers for planners.
            </h2>
            <a href={`mailto:${EMAIL}?subject=Meetings%20%26%20events%20question`} className="mt-6 inline-flex items-center gap-2 text-sm text-forest-900 brass-underline" data-testid="meetings-faq-mailto">
              Something else? Email the front desk <ArrowUpRight size={14} />
            </a>
          </div>
          <div className="md:col-span-8">
            <FaqAccordion items={FAQS} testIdPrefix="meetings-faq" />
          </div>
        </div>
      </section>

      <CtaBlock eyebrow="Ready to plan?" title="One call. One rate. Real answers." />
    </main>
  );
}
